import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import CommentsSection, { Comment } from "./CommentsSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faCalendarAlt,
  faUser,
  faTag,
  faLayerGroup,
  faDiagramProject,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { searchComments } from "./CommentsAPI";
import { CommentDTO ,CommentFilterDTO} from "./CommentsAPI";

interface CommentLocationState {
  context: string;
  entityId: string;
  entityType: string;
  metadata?: Record<string, string | number | undefined>;
  title?: string;
  description?: string;
  uploadType?: string;
  maxFiles?: number;
  allowedFileTypes?: string[];
}

// Map CommentDTO to frontend Comment type
const mapDtoToComment = (dto: CommentDTO): Comment => ({
  id: dto.id || '',
  author: {
    name: dto.username ?? 'Unknown',
  },
  message: dto.message ?? '',
  createdAt: dto.createdAt ?? new Date().toISOString(),
  isOwnComment: false, // Adjust based on current logged-in user if available
  replies: dto.replies?.map(mapDtoToComment) || [],
  attachments: dto.contentUrl ? [dto.contentUrl] : [],
});

const CommentsPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as CommentLocationState;
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const {
    entityType,
    entityId,
    context = "general",
    metadata = {},
    title = "Entity Comments",
  } = state || {};

  useEffect(() => {
    if (entityId && entityType) {
      const fetchComments = async () => {
        try {
          setLoading(true);
          const filter: CommentFilterDTO = {
            entityType,
            entityId,
          };
          const response = await searchComments(filter, 0, 100, 'createdAt,desc');
          const data = response.data?.content || response.data || [];
          const mapped = data.map(mapDtoToComment);
          setComments(mapped);
        } catch (error) {
          console.error("Error loading comments:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchComments();
    }
  }, [entityId, entityType]);

  const renderMetadata = () => {
    return Object.entries(metadata).map(([key, value]) => {
      let icon;

      switch (key.toLowerCase()) {
        case "status":
          icon = faInfoCircle;
          break;
        case "taskname":
        case "title":
          icon = faTag;
          break;
        case "tasklevel":
          icon = faLayerGroup;
          break;
        case "projectname":
          icon = faDiagramProject;
          break;
        case "owner":
          icon = faUser;
          break;
        case "priority":
          icon = faCheckCircle;
          break;
        default:
          icon = faTag;
          break;
      }

      return (
        <p key={key} className="capitalize text-gray-700">
          <FontAwesomeIcon icon={icon} className="mr-2 text-blue-500" />
          {key.replace(/([A-Z])/g, " $1")}:{" "}
          <span className="font-semibold">{String(value)}</span>
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      {/* Header / Entity Details */}
      <div className="max-w-4xl mx-auto bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-800">
            {title || `Comments for ${entityType}`}
          </h1>
          <span className="text-sm text-gray-500">
            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-blue-500" />
            Last updated: {new Date().toLocaleDateString()}
          </span>
        </div>

        {/* Entity Info */}
        <div className="text-sm text-gray-600 mb-4">
          Entity: <strong>{entityType}</strong> | ID: <strong>{entityId}</strong>
        </div>

        {/* Metadata */}
        <div className="text-gray-600 space-y-2">{renderMetadata()}</div>
      </div>

      {/* Comments Section */}
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <p className="text-center text-gray-500">Loading comments...</p>
        ) : (
          <CommentsSection initialComments={comments} entityId={entityId} entityType={entityType} />
        )}
      </div>
    </div>
  );
};

export default CommentsPage;